/**
 * Meta tags dinámicos por ruta (title, description, canonical, OG, robots).
 */

import { Helmet } from "react-helmet-async";
import { absoluteUrl } from "@/lib/seo/siteUrl";

export interface SeoHeadProps {
  title: string;
  description: string;
  /** Ruta relativa, ej. /landing */
  path?: string;
  /** index | noindex */
  robots?: "index, follow" | "noindex, nofollow" | "noindex, follow";
  ogType?: string;
  ogImage?: string;
  /** JSON-LD objects (SoftwareApplication, FAQPage, etc.) */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

export default function SeoHead({
  title,
  description,
  path = "/",
  robots = "index, follow",
  ogType = "website",
  ogImage,
  jsonLd,
}: SeoHeadProps) {
  const canonical = absoluteUrl(path);
  const image = ogImage ?? absoluteUrl("/favicon.svg");
  const ldItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="es_CO" />
      <meta property="og:site_name" content="ALTENOT" />

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {ldItems.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}
